import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "../contexts/FormContext";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { Button } from "../components/Button";
import { Popup } from "../components/Popup";
import MoratoriumGuard from "../components/MoratoriumGuard";

interface WhyChooseItem {
  icon: string;
  text: string;
  iconColor: string;
}

const DEFAULT_LOAN_AMOUNT = 300;

const LoansLanding: React.FC = () => {
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(true);
  const { updateField, validateField } = useForm();
  const [showImportantInfo, setShowImportantInfo] = useState(false);
  const { setIsVisible } = useBottomNavBar();
  const [loanAmount, setLoanAmount] = useState(DEFAULT_LOAN_AMOUNT);

  React.useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, [setIsVisible]);

  const whyChooseItems: WhyChooseItem[] = [
    {
      icon: "favorite_border",
      text: "Search 15+ lenders without impacting your credit score",
      iconColor: "text-blue-500",
    },
    {
      icon: "check_circle_outline",
      text: "Borrow between £100 and £5000, with no fees to apply",
      iconColor: "text-blue-300",
    },
    {
      icon: "star_border",
      text: "Access the credit you need (on approval)",
      iconColor: "text-orange-700",
    },
  ];

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setLoanAmount(value);
  };

  const handleAmountInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    // Allow empty input for better editing experience
    if (value === "") {
      setLoanAmount(0);
      return;
    }

    let numValue = Number(value);
    // Only apply constraints when the input is complete
    if (!isNaN(numValue)) {
      if (numValue > 5000) numValue = 5000;
      if (numValue < 0) numValue = 0;
      setLoanAmount(numValue);
    }
  };

  const handleAmountInputBlur = () => {
    // Apply minimum constraint only when input loses focus
    if (loanAmount < 100) {
      setLoanAmount(100);
    }
  };

  const handleApplyClick = () => {
    const error = validateField("borrow_amount", loanAmount.toString());
    if (!error) {
      // Only persist the loan amount when navigating to apply page
      updateField("borrow_amount", loanAmount.toString());
      navigate("/apply");
    }
  };

  return (
    <div className="content-container">
      {/* <main className="container mx-auto"> */}
      {/* Warning Banner */}
      <div className="bg-grey-40 rounded-20 p-4 mb-6 max-w-3xl mx-auto">
        <p className="text-center">
          <span className="font-bold">Warning: </span>
          Late repayment can cause you serious money problems. For help go to{" "}
          <a
            href="https://www.moneyhelper.org.uk"
            className="font-bold underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            moneyhelper.org.uk
          </a>
        </p>
      </div>

      {/* Loan Form Section */}
      <div className="bg-white rounded-20 py-8 px-3 md:px-8 mb-6 max-w-3xl mx-auto border border-black">
        <h1 className="text-4xl font-bold text-black text-center mb-4">
          How much money do you need?
        </h1>
        <p
          className="text-black text-center font-bold mb-6 cursor-pointer"
          onClick={() => setShowImportantInfo(true)}
        >
          Please read the important information{" "}
          <span className="inline-block align-middle">ⓘ</span>
        </p>

        <div className="bg-white rounded-2xl p-6">
          <div className="mb-6">
            <label className="form-label">How much do you need?</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
                £
              </span>
              <input
                name="loan_amount"
                type="number"
                value={loanAmount}
                onChange={handleAmountInputChange}
                onBlur={handleAmountInputBlur}
                className="form-input-secondary pl-6"
              />
            </div>
          </div>

          <input
            type="range"
            name="loan_amount_slider"
            min="100"
            max="5000"
            step="10"
            value={loanAmount}
            onChange={handleSliderChange}
            className="loan-range-slider"
          />

          <div className="flex justify-between mt-2 mb-6">
            <span className="text-sm">£100</span>
            <span className="text-sm">£5000</span>
          </div>

          <Button
            text="Apply now"
            onClick={handleApplyClick}
            className="w-full"
          />

          <p className="text-center mt-4 font-bold">Representative 1333%APR</p>
        </div>
      </div>

      {/* Why Choose Afforda Section */}
      <div className=" rounded-2xl py-8 px-3 md:px-8 mb-6 max-w-3xl mx-auto bg-grey-40">
        <h2 className="text-3xl font-bold text-center mb-6">
          Why choose Afforda?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {whyChooseItems.map((item, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-2xl p-4 text-center bg-white"
            >
              <span
                className={`material-icons text-5xl ${item.iconColor} mb-4`}
              >
                {item.icon}
              </span>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-white rounded-2xl py-8 px-3 md:px-8 mb-6 max-w-3xl mx-auto border border-gray-200">
        <h2 className="text-3xl font-bold text-center mb-6">
          How does it work?
        </h2>
        <div className="space-y-6">
          <div className="flex items-start">
            <span className="material-icons text-black text-5xl mr-4">
              looks_one
            </span>
            <div>
              <h3 className="font-bold text-xl">Complete our simple application</h3>
              <p>
                Spend just a few minutes filling out our short and secure form.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <span className="material-icons text-black text-5xl mr-4">
              looks_two
            </span>
            <div>
              <h3 className="font-bold text-xl">We do the leg work</h3>
              <p>
                We'll search our panel of lenders for a credit product that
                suits your needs.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <span className="material-icons text-black text-5xl mr-4">
              looks_3
            </span>
            <div>
              <h3 className="font-bold text-xl">Get your cash</h3>
              <p>
                If you're accepted, make sure you review the offer from the
                lender. Once the documents are signed, the funds could be in
                your bank account within 90 seconds. However, actual payout
                times depend on the lender.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info Sections */}
      <div className="space-y-6 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl py-4 px-3 md:px-8 border-2 border-blue-500">
          <h3 className="text-3xl font-bold mb-4">
            Can you find me a credit product?
          </h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Afforda works with over 15 lenders to try to find you the money
              you need.
            </li>
            <li>
              We work with payday loan lenders, short-term loan companies,
              credit lines firms and others to try to get you the credit you
              need.
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-2xl py-4 px-3 md:px-8 border-2 border-blue-500">
          <h3 className="text-3xl font-bold mb-4">
            How does the loan process work?
          </h3>
          <p className="mb-2">Through Afforda, you could get access to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Short term loans</li>
            <li>Payday loans</li>
            <li>Emergency loans</li>
            <li>Credit line firms</li>
            <li>Cash Advance options</li>
          </ul>
        </div>

        <div className="bg-white rounded-2xl py-4 px-3 md:px-8 border-2 border-blue-500">
          <h3 className="text-3xl font-bold mb-4">
            What should I consider before applying?
          </h3>
          <ul className="list-disc pl-6 space-y-4">
            <li className="font-bold">
              Any form of borrowing should be considered carefully. It's
              important that you only borrow what you need.
            </li>
            <li>
              Our partners offer credit products that can be a helpful solution
              for covering unexpected expenses or temporary financial gaps.
              However, it's important to use these products responsibly.
            </li>
            <li className="font-bold">
              Before you apply for a credit product, carefully consider your
              ability to make the repayments.
            </li>
            <li>
              You must read and understand the terms and conditions of the
              product.
            </li>
          </ul>
        </div>
      </div>
      {/* </main> */}

      {/* Important Information Popup */}
      {(showImportantInfo || showDisclaimer) && (
        <Popup
          isOpen={showImportantInfo || showDisclaimer}
          onClose={() => {
            setShowImportantInfo(false);
            setShowDisclaimer(false);
          }}
          closeOnBackdropClick={false}
          title="Important Information"
          buttons={[
            {
              text: "Continue",
              onClick: () => {
                setShowImportantInfo(false);
                setShowDisclaimer(false);
              },
              variant: "secondary",
            },
            {
              text: "Go back",
              onClick: () => {
                if (showDisclaimer) {
                  navigate("/customer-home");
                } else {
                  setShowImportantInfo(false);
                }
              },
              variant: "light",
            },
          ]}
          maxWidth="2xl"
          maxHeight="80vh"
        >
          <div className="space-y-4">
            <h3 className="font-bold text-xl mt-2">About loans with Afforda</h3>
            <p className="mb-4">
              GAIN Credit LLC, trading as Afforda is a credit broker, not a
              lender. We connect you with credit options from various finance
              providers.
            </p>
            <p className="mb-4">
              We compare products from over 15 lenders to see if it's possible
              to get you an offer.
            </p>
            <p className="mb-4">
              <span className="font-bold">Contact address:</span> Afforda, PO
              Box 10756, Leicester, LE3 4GX
            </p>

            <div className="p-4 bg-grey-20 rounded-20">
              <h3 className="font-bold text-xl mb-2">Representative example</h3>
              <p>
                Given a Representative APR of{" "}
                <span className="font-bold">1333%</span>, if you borrow{" "}
                <span className="font-bold">£300</span> over{" "}
                <span className="font-bold">6 months</span>, at{" "}
                <span className="font-bold">292.0%</span> p.a. (fixed), you
                would owe <span className="font-bold">6</span> equal repayments
                of <span className="font-bold">£96.66</span>.
              </p>
              <p className="pt-3">
                You would repay <span className="font-bold">£579.96</span> in
                total.
              </p>
            </div>

            <h3 className="font-bold text-xl">Important considerations</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Not all individuals will be approved for credit. An offer of
                credit is subject to status and meeting the lender's criteria.
              </li>
              <li>
                If you are offered a rate, this will depend on your unique
                situation.
              </li>
              <li>
                You may be able to borrow between £100 and £5,000, with
                repayment terms from over 61 days to 60 months.
              </li>
            </ul>

            <h3 className="font-bold text-xl">Fees and commissions</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Afforda does not charge you for using our service - we receive a
                commission from lenders or companies we introduce you to.
              </li>
              <li>
                The amount we receive depends on the product and amount
                borrowed.
              </li>
              <li>
                Any commission we receive does not affect the price you pay.
              </li>
            </ul>

            <h3 className="font-bold text-xl">Other services</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                If we are unable to find you a loan or line of credit, we may
                transfer you directly to alternative firms and services such as
                credit builder tools.
              </li>
              <li>
                These tools may incur a monthly fee if you choose to use them.
              </li>
            </ul>
          </div>
        </Popup>
      )}
      {/* Rendered last so it stacks above the disclaimer popup */}
      <MoratoriumGuard />
    </div>
  );
};

export default LoansLanding;
